/**
 * Lexical Toolbar Plugin
 * Comprehensive toolbar with all formatting options
 */

import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
    $getSelection,
    $isRangeSelection,
    $createParagraphNode,
    FORMAT_TEXT_COMMAND,
    FORMAT_ELEMENT_COMMAND,
    UNDO_COMMAND,
    REDO_COMMAND,
    CAN_UNDO_COMMAND,
    CAN_REDO_COMMAND,
    COMMAND_PRIORITY_CRITICAL,
} from 'lexical';
import {
    $isHeadingNode,
    $createHeadingNode,
    $createQuoteNode,
} from '@lexical/rich-text';
import {
    $isListNode,
    INSERT_ORDERED_LIST_COMMAND,
    INSERT_UNORDERED_LIST_COMMAND,
    REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $setBlocksType } from '@lexical/selection';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/react/LexicalHorizontalRuleNode';
import { INSERT_IMAGE_COMMAND } from '../plugins/ImagesPlugin';

const FONT_FAMILY_OPTIONS = [
    ['Arial', 'Arial'],
    ['Georgia', 'Georgia'],
    ['Times New Roman', 'Times New Roman'],
    ['Courier New', 'Courier New'],
    ['Verdana', 'Verdana'],
    ['Trebuchet MS', 'Trebuchet MS'],
    ['Impact', 'Impact'],
];

const FONT_SIZE_OPTIONS = [
    ['10px', '10'],
    ['12px', '12'],
    ['14px', '14'],
    ['16px', '16'],
    ['18px', '18'],
    ['20px', '20'],
    ['24px', '24'],
    ['30px', '30'],
    ['36px', '36'],
    ['48px', '48'],
    ['60px', '60'],
    ['72px', '72'],
];

const BLOCK_TYPE_OPTIONS = [
    ['paragraph', 'Normal'],
    ['h1', 'Heading 1'],
    ['h2', 'Heading 2'],
    ['h3', 'Heading 3'],
    ['h4', 'Heading 4'],
    ['h5', 'Heading 5'],
    ['h6', 'Heading 6'],
    ['quote', 'Quote'],
];

const TEXT_COLOR_OPTIONS = [
    '#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#efefef', '#f3f3f3', '#ffffff',
    '#980000', '#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#4a86e8', '#0000ff', '#9900ff', '#ff00ff',
    '#e6b8af', '#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3', '#d0e0e3', '#c9daf8', '#cfe2f3', '#d9d2e9', '#ead1dc',
    '#dd7e6b', '#ea9999', '#f9cb9c', '#ffe599', '#b6d7a8', '#a2c4c9', '#a4c2f4', '#9fc5e8', '#b4a7d6', '#d5a6bd',
];

const BG_COLOR_OPTIONS = [
    'transparent', '#ffffff', '#ffffc0', '#ffd9b3', '#ffb3b3', '#e6b3ff', '#b3d9ff', '#b3ffb3', '#cccccc',
    '#ffff00', '#ff9900', '#ff0000', '#9900ff', '#0066ff', '#00cc00', '#666666', '#000000',
];

function Divider() {
    return <div className="toolbar-divider" />;
}

function DropdownButton({ label, icon, children, className = '' }) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className={`toolbar-dropdown ${className}`} ref={dropdownRef}>
            <button
                type="button"
                className="toolbar-btn toolbar-dropdown-btn"
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
            >
                {icon && <span className="toolbar-icon">{icon}</span>}
                <span className="dropdown-label">{label}</span>
                <span className="dropdown-arrow">▼</span>
            </button>
            {isOpen && (
                <div className="dropdown-menu show">
                    {React.Children.map(children, child => 
                        React.cloneElement(child, { 
                            onClick: (e) => {
                                child.props.onClick?.(e);
                                setIsOpen(false);
                            }
                        })
                    )}
                </div>
            )}
        </div>
    );
}

function ColorPicker({ color, onChange, colors, label, icon }) {
    const [isOpen, setIsOpen] = useState(false);
    const pickerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="toolbar-color-picker" ref={pickerRef}>
            <button
                type="button"
                className="toolbar-btn toolbar-color-btn"
                onClick={() => setIsOpen(!isOpen)}
                title={label}
            >
                <span className="color-icon">{icon}</span>
                <span 
                    className="color-indicator" 
                    style={{ backgroundColor: color || '#000000' }}
                />
            </button>
            {isOpen && (
                <div className="color-dropdown show">
                    <div className="color-grid">
                        {colors.map((c, i) => (
                            <button
                                key={i}
                                type="button"
                                className="color-option"
                                style={{ backgroundColor: c === 'transparent' ? '#fff' : c }}
                                onClick={() => {
                                    onChange(c);
                                    setIsOpen(false);
                                }}
                                title={c}
                            />
                        ))}
                    </div>
                    <input
                        type="color"
                        className="color-custom"
                        value={color || '#000000'}
                        onChange={(e) => {
                            onChange(e.target.value);
                            setIsOpen(false);
                        }}
                        title="Custom color"
                    />
                </div>
            )}
        </div>
    );
}

export default function ToolbarPlugin({ editorAPI }) {
    const [editor] = useLexicalComposerContext();
    
    // State
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const [blockType, setBlockType] = useState('paragraph');
    const [fontFamily, setFontFamily] = useState('Arial');
    const [fontSize, setFontSize] = useState('14');
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isStrikethrough, setIsStrikethrough] = useState(false);
    const [isCode, setIsCode] = useState(false);
    const [isLink, setIsLink] = useState(false);
    const [textColor, setTextColor] = useState('#000000');
    const [bgColor, setBgColor] = useState('transparent');
    const [elementFormat, setElementFormat] = useState('left');

    // Update toolbar state based on selection
    const updateToolbar = useCallback(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            // Text format
            setIsBold(selection.hasFormat('bold'));
            setIsItalic(selection.hasFormat('italic'));
            setIsUnderline(selection.hasFormat('underline'));
            setIsStrikethrough(selection.hasFormat('strikethrough'));
            setIsCode(selection.hasFormat('code'));

            // Block type
            const anchorNode = selection.anchor.getNode();
            let element = anchorNode.getKey() === 'root'
                ? anchorNode
                : $findMatchingParent(anchorNode, (e) => {
                    const parent = e.getParent();
                    return parent !== null && parent.getKey() === 'root';
                });

            if (element !== null) {
                if ($isListNode(element)) {
                    const type = element.getListType();
                    setBlockType(type === 'number' ? 'ol' : 'ul');
                } else if ($isHeadingNode(element)) {
                    const tag = element.getTag();
                    setBlockType(tag);
                } else {
                    const type = element.getType();
                    setBlockType(type === 'quote' ? 'quote' : 'paragraph');
                }
            }

            // Link
            const node = selection.anchor.getNode();
            const parent = node.getParent();
            setIsLink($isLinkNode(parent) || $isLinkNode(node));
        }
    }, []);

    // Register listeners
    useEffect(() => {
        return mergeRegister(
            editor.registerCommand(
                CAN_UNDO_COMMAND,
                (payload) => {
                    setCanUndo(payload);
                    return false;
                },
                COMMAND_PRIORITY_CRITICAL
            ),
            editor.registerCommand(
                CAN_REDO_COMMAND,
                (payload) => {
                    setCanRedo(payload);
                    return false;
                },
                COMMAND_PRIORITY_CRITICAL
            ),
            editor.registerUpdateListener(({ editorState }) => {
                editorState.read(() => {
                    updateToolbar();
                });
            })
        );
    }, [editor, updateToolbar]);

    // Format handlers
    const formatHeading = useCallback((headingSize) => {
        editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                if (headingSize === 'paragraph') {
                    $setBlocksType(selection, () => $createParagraphNode());
                } else if (headingSize === 'quote') {
                    $setBlocksType(selection, () => $createQuoteNode());
                } else {
                    $setBlocksType(selection, () => $createHeadingNode(headingSize));
                }
            }
        });
    }, [editor]);

    const applyStyleText = useCallback((styles) => {
        editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                selection.getNodes().forEach((node) => {
                    if (node.setStyle) {
                        const currentStyle = node.getStyle() || '';
                        const newStyle = currentStyle + Object.entries(styles)
                            .map(([key, value]) => `${key}: ${value}`)
                            .join('; ') + ';';
                        node.setStyle(newStyle);
                    }
                });
            }
        });
    }, [editor]);

    const insertLink = useCallback(() => {
        if (isLink) {
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
        } else {
            const url = prompt('Enter URL:');
            if (url) {
                editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
            }
        }
    }, [editor, isLink]);

    const insertImage = useCallback(() => {
        // Check if there's a custom image handler
        if (editorAPI._toolbarHandlers && editorAPI._toolbarHandlers.image) {
            editorAPI._toolbarHandlers.image();
            return;
        }

        const url = prompt('Enter image URL:');
        if (url) {
            editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
                src: url,
                altText: 'Image',
            });
        }
    }, [editor, editorAPI]);

    const handleFontSizeChange = useCallback((delta) => {
        const newSize = Math.max(8, Math.min(96, parseInt(fontSize) + delta));
        setFontSize(String(newSize));
        applyStyleText({ 'font-size': `${newSize}px` });
    }, [fontSize, applyStyleText]);

    return (
        <div className="lexical-toolbar">
            {/* Undo/Redo */}
            <div className="toolbar-group">
                <button
                    type="button"
                    className="toolbar-btn"
                    disabled={!canUndo}
                    onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
                    title="Undo"
                >
                    ↶
                </button>
                <button
                    type="button"
                    className="toolbar-btn"
                    disabled={!canRedo}
                    onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
                    title="Redo"
                >
                    ↷
                </button>
            </div>

            <Divider />

            {/* Block Type */}
            <div className="toolbar-group">
                <DropdownButton 
                    label={BLOCK_TYPE_OPTIONS.find(([key]) => key === blockType)?.[1] || 'Normal'}
                >
                    {BLOCK_TYPE_OPTIONS.map(([value, label]) => (
                        <div
                            key={value}
                            className={`dropdown-option ${blockType === value ? 'active' : ''}`}
                            onClick={() => formatHeading(value)}
                        >
                            {label}
                        </div>
                    ))}
                </DropdownButton>
            </div>

            <Divider />

            {/* Font Family */}
            <div className="toolbar-group">
                <select
                    className="toolbar-select toolbar-font-family"
                    value={fontFamily}
                    onChange={(e) => {
                        setFontFamily(e.target.value);
                        applyStyleText({ 'font-family': e.target.value });
                    }}
                    title="Font Family"
                >
                    {FONT_FAMILY_OPTIONS.map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                    ))}
                </select>
            </div>

            <Divider />

            {/* Font Size */}
            <div className="toolbar-group">
                <div className="toolbar-font-size-group">
                    <button
                        type="button"
                        className="toolbar-btn"
                        onClick={() => handleFontSizeChange(-1)}
                        title="Decrease font size"
                    >
                        −
                    </button>
                    <input
                        type="text"
                        className="toolbar-font-size-input"
                        value={fontSize}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            setFontSize(value);
                        }}
                        onBlur={() => {
                            const size = Math.max(8, Math.min(96, parseInt(fontSize) || 14));
                            setFontSize(String(size));
                            applyStyleText({ 'font-size': `${size}px` });
                        }}
                        title="Font size"
                    />
                    <button
                        type="button"
                        className="toolbar-btn"
                        onClick={() => handleFontSizeChange(1)}
                        title="Increase font size"
                    >
                        +
                    </button>
                </div>
            </div>

            <Divider />

            {/* Text Formatting */}
            <div className="toolbar-group">
                <button
                    type="button"
                    className={`toolbar-btn ${isBold ? 'active' : ''}`}
                    onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
                    title="Bold"
                >
                    <b>B</b>
                </button>
                <button
                    type="button"
                    className={`toolbar-btn ${isItalic ? 'active' : ''}`}
                    onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
                    title="Italic"
                >
                    <i>I</i>
                </button>
                <button
                    type="button"
                    className={`toolbar-btn ${isUnderline ? 'active' : ''}`}
                    onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
                    title="Underline"
                >
                    <u>U</u>
                </button>
                <button
                    type="button"
                    className={`toolbar-btn ${isStrikethrough ? 'active' : ''}`}
                    onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
                    title="Strikethrough"
                >
                    <s>S</s>
                </button>
            </div>

            <Divider />

            {/* Code & Link */}
            <div className="toolbar-group">
                <button
                    type="button"
                    className={`toolbar-btn ${isCode ? 'active' : ''}`}
                    onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')}
                    title="Code"
                >
                    &lt;&gt;
                </button>
                <button
                    type="button"
                    className={`toolbar-btn ${isLink ? 'active' : ''}`}
                    onClick={insertLink}
                    title="Link"
                >
                    🔗
                </button>
            </div>

            <Divider />

            {/* Colors */}
            <div className="toolbar-group">
                <ColorPicker
                    color={textColor}
                    onChange={(color) => {
                        setTextColor(color);
                        applyStyleText({ color });
                    }}
                    colors={TEXT_COLOR_OPTIONS}
                    label="Text Color"
                    icon="A"
                />
                <ColorPicker
                    color={bgColor}
                    onChange={(color) => {
                        setBgColor(color);
                        applyStyleText({ 'background-color': color });
                    }}
                    colors={BG_COLOR_OPTIONS}
                    label="Background Color"
                    icon="🖌"
                />
            </div>

            <Divider />

            {/* Alignment */}
            <div className="toolbar-group">
                <DropdownButton label={`☰ ${elementFormat.charAt(0).toUpperCase() + elementFormat.slice(1)}`}>
                    {['left', 'center', 'right', 'justify'].map((align) => (
                        <div
                            key={align}
                            className={`dropdown-option ${elementFormat === align ? 'active' : ''}`}
                            onClick={() => {
                                setElementFormat(align);
                                editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, align);
                            }}
                        >
                            {align.charAt(0).toUpperCase() + align.slice(1)}
                        </div>
                    ))}
                </DropdownButton>
            </div>

            <Divider />

            {/* Insert */}
            <div className="toolbar-group">
                <DropdownButton label="+ Insert">
                    <div className="dropdown-option" onClick={insertImage}>
                        🖼 Image
                    </div>
                    <div 
                        className="dropdown-option" 
                        onClick={() => editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined)}
                    >
                        ─ Horizontal Rule
                    </div>
                    <div 
                        className="dropdown-option" 
                        onClick={() => formatHeading('quote')}
                    >
                        ❝ Block Quote
                    </div>
                    <div 
                        className="dropdown-option" 
                        onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)}
                    >
                        • Bullet List
                    </div>
                    <div 
                        className="dropdown-option" 
                        onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)}
                    >
                        1. Numbered List
                    </div>
                </DropdownButton>
            </div>
        </div>
    );
}
